import { extendTheme, defineStyleConfig } from "@chakra-ui/react";

/**
 * Hostea Theme - Simplified
 *
 * Uses Chakra UI's built-in props wherever possible:
 * - Text: use fontSize="sm", color="gray.500", fontWeight="semibold" directly
 * - Heading: use size="2xl", size="lg" directly (Chakra sizes: 4xl, 3xl, 2xl, xl, lg, md, sm, xs)
 * - Box: use bg, p, borderRadius props directly
 *
 * Only custom configurations that can't be achieved with props are defined here.
 */

const mainTheme = extendTheme({
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },

  fonts: {
    heading: "'Poppins', sans-serif",
    body: "'Poppins', sans-serif",
  },

  // Custom color palette for dark/light modes
  colors: {
    dark: {
      primary: "#23272f",
      secondary: "#181a20",
      text: "#ffffff",
      muted: "#ffffffcc",
    },
    light: {
      primary: "#ffffff",
      secondary: "#f7fafc",
      text: "#2d3748",
      muted: "#718096",
    },
  },

  // Custom breakpoints (specific to Hostea)
  breakpoints: {
    sm: "22.563em", // 360px
    md: "30em",     // 480px
    lg: "90.5em",   // 1440px
    xl: "120em",    // 1920px
  },

  // Global styles
  styles: {
    global: (props) => ({
      body: {
        bg: props.colorMode === 'dark' ? '#23272f' : 'white',
        color: props.colorMode === 'dark' ? '#f7fafc' : '#2d3748',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      },
      // Toast slide-in animation
      '@keyframes slideInDown': {
        '0%': {
          opacity: 0,
          transform: 'translateY(-12px) scale(0.96)',
        },
        '100%': {
          opacity: 1,
          transform: 'translateY(0) scale(1)',
        },
      },
    }),
  },

  components: {
    // Button - custom border radius and hover effect
    Button: {
      baseStyle: {
        fontWeight: "600",
        borderRadius: "xl",
      },
      variants: {
        solid: () => ({
          bg: '#3182ce',
          color: 'white',
          _hover: {
            bg: '#2c5aa0',
            transform: 'translateY(-2px)',
            boxShadow: 'lg',
          },
          transition: 'all 0.3s ease',
        }),
      },
    },

    // Card - custom hover effect
    Card: {
      baseStyle: (props) => ({
        container: {
          bg: props.colorMode === 'dark' ? '#181a20' : '#f7fafc',
          border: '1px solid',
          borderColor: props.colorMode === 'dark' ? '#4a5568' : '#e2e8f0',
          borderRadius: '2xl',
          transition: 'all 0.3s ease',
          _hover: {
            transform: 'translateY(-2px)',
            boxShadow: props.colorMode === 'dark' ? 'xl' : 'lg',
          },
        },
      }),
    },

    // Heading - base style + useful variants
    Heading: {
      baseStyle: (props) => ({
        color: props.colorMode === 'dark' ? 'white' : 'gray.800',
        letterSpacing: '-0.02em',
      }),
      variants: {
        // Page title (large)
        page: {
          fontSize: ['2xl', '3xl', '3xl', '4xl'],
          fontWeight: '700',
        },
        // Section title
        section: {
          fontSize: ['xl', '2xl', '2xl', '2xl'],
          fontWeight: '600',
        },
        // Subtitle (smaller, muted)
        subtitle: (props) => ({
          fontSize: ['sm', 'md', 'md', 'md'],
          fontWeight: '500',
          color: props.colorMode === 'dark' ? 'whiteAlpha.800' : 'gray.500',
        }),
      },
    },

    // Text - empty baseStyle allows color inheritance from parent
    // Variants for commonly used text styles
    Text: {
      baseStyle: {
        // Inherits color from parent by default
      },
      variants: {
        // Secondary text (gray)
        muted: (props) => ({
          color: props.colorMode === 'dark' ? 'whiteAlpha.800' : 'gray.500',
          fontSize: 'sm',
        }),
        // Very subtle text (lighter gray)
        subtle: (props) => ({
          color: props.colorMode === 'dark' ? 'whiteAlpha.700' : 'gray.400',
          fontSize: 'xs',
        }),
        // Form labels
        label: (props) => ({
          color: props.colorMode === 'dark' ? 'whiteAlpha.800' : 'gray.500',
          fontSize: 'xs',
        }),
      },
    },

    // Image - specific logo sizes needed for navbar consistency
    Image: defineStyleConfig({
      baseStyle: {
        objectFit: 'contain',
      },
      variants: {
        logo: {
          objectFit: 'contain',
        },
        'logo-nav-mobile': {
          objectFit: 'contain',
          w: ['22px', '24px', '28px', '32px', '32px'],
          h: ['22px', '24px', '28px', '32px', '32px'],
        },
        'logo-nav-desktop': {
          objectFit: 'contain',
          w: ['28px', '30px', '32px', '36px', '36px'],
          h: ['28px', '30px', '32px', '36px', '36px'],
        },
        'logo-nav-collapsed': {
          objectFit: 'contain',
          w: ['20px', '22px', '24px', '26px', '26px'],
          h: ['20px', '22px', '24px', '26px', '26px'],
        },
        'logo-drawer': {
          objectFit: 'contain',
          w: ['28px', '30px', '32px', '36px', '36px'],
          h: ['28px', '30px', '32px', '36px', '36px'],
        },
        avatar: {
          borderRadius: 'full',
          objectFit: 'cover',
        },
        card: {
          borderRadius: '12px',
          objectFit: 'cover',
        },
        rounded: {
          borderRadius: 'full',
          objectFit: 'cover',
        },
      },
      defaultProps: {
        variant: 'logo',
      },
    }),

    // Tabs - fix for text color inheritance
    Tabs: {
      variants: {
        'soft-rounded': {
          tab: {
            _selected: {
              '& *': { color: 'inherit' },
            },
          },
        },
      },
    },

    // Alert — All toasts now use useHosteaToast (custom render) so the
    // old dark glassmorphism style is no longer needed. Keeping Alert at
    // Chakra defaults so inline <Alert> components look normal.
  },
});

export default mainTheme;
